import React, { useEffect, useState, createRef } from "react";
import { makeStyles } from "@material-ui/core";
import { Chart, CommonSeriesSettings, Legend, ArgumentAxis, Label, Series, Tooltip, ValueAxis, Grid, MinorGrid, CommonPaneSettings, Export, Border, ZoomAndPan } from "devextreme-react/chart";
import { controlTypes, externalApiTypes, timeLineVisualisationTypes, valueTypeCodes } from "../../../../Config/constants";
import { parseNewDateToDDMMYYYnoTime } from "../../../../Utils/dateHelper";
import { ArrowRight } from "lucide-react";
import Button from "../../../Shared/Buttons/Button";

export default function FilterListRow({ loading, ...props }) {
  const classes = useStyles();
  const [visualStartTime, setVisualStartTime] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Subtract 1 day
    return yesterday;
  });
  const [visualEndTime, setVisualEndTime] = useState(new Date());
  const [labelData, setLabelData] = useState([]);

  const [visualisationType, setVisualisationType] = useState(timeLineVisualisationTypes.SPLIT);
  const [chartView, setChartView] = useState("timeline"); // "timeline" | "events"

  useEffect(() => {
    if (props.visualisationType) {
      setVisualisationType(props.visualisationType);
    }
  }, [props.visualisationType]);

  useEffect(() => {
    setLabelData([]);
  }, [props.timeRange, props.refresh, props.endValue, props.startValue]);

  function getRGB(c) {
    return parseInt(c, 16) || c;
  }

  function getsRGB(c) {
    return getRGB(c) / 255 <= 0.03928 ? getRGB(c) / 255 / 12.92 : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4);
  }

  function getLuminance(hexColor) {
    if (hexColor.includes("#") && hexColor !== undefined) {
      return 0.2126 * getsRGB(hexColor.substr(1, 2)) + 0.7152 * getsRGB(hexColor.substr(3, 2)) + 0.0722 * getsRGB(hexColor.substr(-2));
    } else {
      return 0.2126 * hexColor + 0.7152 * hexColor + 0.0722 * hexColor;
    }
  }

  function getContrast(f, b) {
    const L1 = getLuminance(f);
    const L2 = getLuminance(b);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  }

  function getTextColor(bgColor) {
    if (bgColor === null) {
      return "#ffffff";
    } else {
      const whiteContrast = getContrast(bgColor, "#ffffff");
      const blackContrast = getContrast(bgColor, "#000000");
      return whiteContrast > blackContrast ? "#ffffff" : "#000000";
    }
  }

  function customizeValueAxis(e) {
    if (e.valueText.includes("PM") || e.valueText.includes("AM")) {
      let date = new Date(e.value);
      let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
      let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
      let seconds = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
      let time = hours + ":" + minutes + ":" + seconds;
      let htmlItem = "";
      htmlItem += '<div style="padding-right: 5px">' + time + "</div><br>";
      return htmlItem;
    } else {
      if (e.valueText.includes("s")) {
        let date = new Date(e.value);
        let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
        let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
        let seconds = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
        let time = hours + ":" + minutes + ":" + seconds;
        let htmlItem = "";
        htmlItem += '<div style="padding-right: 5px">' + time + "</div><br>";
        return htmlItem;
      } else {
        let date = new Date(e.value);
        let strDay = date.toLocaleDateString("en-UK", {
          weekday: "long",
        });
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Adjust for zero-based month
        const year = date.getFullYear();
        // Build the HTML string using template literals
        const htmlItem = `<div>${strDay}</div><div>${day}/${month}/${year}</div>`;
        return htmlItem;
      }
    }
  }

  function getDetailDescription(data) {
    return data.description || data.counterDescription || data.freetextDescription ||
      data.datalistDescription || data.cameraDescription || data.externalApiDescription || "-";
  }

  function customizeTooltip(pointInfo) {
    let strTime = pointInfo.valueText;
    let insertedOn = pointInfo.point.data.insertedOn;
    let updatedOn = pointInfo.point.data.updatedOn;
    let times = strTime.split(" - ");
    let startTime = times[0];

    var date = parseNewDateToDDMMYYYnoTime(new Date(startTime));

    let startDateTime = new Date(pointInfo.point.data.startDate);
    let endDateTime = new Date(pointInfo.point.data.endDate);

    let endTime = times[1];
    let value = "";
    let strStartTime = convertToTimeString(startTime);
    let strEndTime = convertToTimeString(endTime);
    let time = "";
    let commentText = pointInfo.point.data.commentText;
    var commentImage;
    var commentImageFound = false;
    if (pointInfo.point.data.commentImage !== "" && pointInfo.point.data.commentImage !== null) {
      commentImageFound = true;
      commentImage = props.data.find((x) => x.id === pointInfo.point.data.id).commentImage;
    }
    if (pointInfo.point.data.typeId === controlTypes.STOPWATCH || (pointInfo.point.data.typeId === controlTypes.EXTERNALAPI && pointInfo.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      strStartTime = convertToTimeString(startDateTime);
      strEndTime = convertToTimeString(endDateTime);

      value = pointInfo.point.data.measurementValue;
      time = strStartTime + " - " + strEndTime;
    } else {
      value = pointInfo.point.data.measurementValue;
      time = strStartTime;
    }
    let text = "";
    if (commentImageFound) {
      text += '<div style="min-height: 350px;">';
    } else {
      text += "<div>";
    }
    text += `<div><b>Project:</b> ${pointInfo.point.data.projectName}</div>`;
    text += `<div><b>Company: </b>${pointInfo.point.data.companyName}</div>`;
    text += `<div><b>Registration point: </b>${pointInfo.point.data.resource}</div>`;
    text += `<div><b>Measurement: </b>${getDetailDescription(pointInfo.point.data)}</div>`;
    text += `<div><b>Time: </b>${time}</div>`;
    text += `<div><b>Date: </b>${date}</div>`;
    text += `<div><b>Key: </b>${pointInfo.point.data.key !== null ? pointInfo.point.data.key : "-"}</div>`;

    if (pointInfo.point.data.commentText !== "" && pointInfo.point.data.commentText !== null) {
      text += `<div style="padding-bottom: 5px"><b>Comment: </b>${commentText}</div>`;
    }
    if (commentImageFound) {
      text += `<div style="padding-bottom: 5px"><b>Comment Image: </b></div>`;
      text += `<img style="border-radius: 5px; max-width: 100%; max-height: calc(20vh - 16px);" src="data:image/png;base64,${commentImage}"></img>`;
    }

    if (pointInfo.point.data.typeId === controlTypes.CAMERA && pointInfo.point.data.thumbnail !== null) {
      text += `<img  src="data:image/png;base64,${pointInfo.point.data.thumbnail}"></img>`;
    }
    text += "</div>";

    return { text: `${pointInfo.argumentText}<br/> ${time}`, html: text, visible: true };
  }

  function customizeLinearTooltip(pointInfo) {
    let strTime = pointInfo.valueText;
    let insertedOn = pointInfo.point.data.insertedOn;
    let updatedOn = pointInfo.point.data.updatedOn;
    let times = strTime.split(" - ");
    let startTime = times[0];

    var date = parseNewDateToDDMMYYYnoTime(new Date(startTime));

    let startDateTime = new Date(pointInfo.point.data.startDate);
    let endDateTime = new Date(pointInfo.point.data.endDate);

    let endTime = times[1];
    let value = "";
    let strStartTime = convertToTimeString(startTime);
    let strEndTime = convertToTimeString(endTime);
    let time = "";
    let commentText = pointInfo.point.data.commentText;
    var commentImage;
    var commentImageFound = false;
    if (pointInfo.point.data.commentImage !== "" && pointInfo.point.data.commentImage !== null) {
      commentImageFound = true;
      commentImage = props.data.find((x) => x.id === pointInfo.point.data.id).commentImage;
    }
    if (pointInfo.point.data.typeId === controlTypes.STOPWATCH || (pointInfo.point.data.typeId === controlTypes.EXTERNALAPI && pointInfo.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      strStartTime = convertToTimeString(startDateTime);
      strEndTime = convertToTimeString(endDateTime);

      value = pointInfo.point.data.measurementValue;
      time = strStartTime + " - " + strEndTime;
    } else {
      value = pointInfo.point.data.measurementValue;
      time = strStartTime;
    }
    let text = "";
    if (commentImageFound) {
      text += '<div style="min-height: 350px;">';
    } else {
      text += "<div>";
    }
    text += `<div><b>Value:</b> ${pointInfo.point.data.measurementValue}</div>`;
    text += `<div><b>Project:</b> ${pointInfo.point.data.projectName}</div>`;
    text += `<div><b>Company: </b>${pointInfo.point.data.companyName}</div>`;
    text += `<div><b>Registration point: </b>${pointInfo.point.data.resource}</div>`;
    text += `<div><b>Measurement: </b>${getDetailDescription(pointInfo.point.data)}</div>`;
    text += `<div><b>Time: </b>${time}</div>`;
    text += `<div><b>Date: </b>${date}</div>`;
    text += `<div><b>Key: </b>${pointInfo.point.data.key !== null ? pointInfo.point.data.key : "-"}</div>`;

    if (pointInfo.point.data.commentText !== "" && pointInfo.point.data.commentText !== null) {
      text += `<div style="padding-bottom: 5px"><b>Comment: </b>${commentText}</div>`;
    }
    if (commentImageFound) {
      text += `<div style="padding-bottom: 5px"><b>Comment Image: </b></div>`;
      text += `<img style="border-radius: 5px; max-width: 100%; max-height: calc(20vh - 16px);" src="data:image/png;base64,${commentImage}"></img>`;
    }

    if (pointInfo.point.data.typeId === controlTypes.CAMERA && pointInfo.point.data.thumbnail !== null) {
      text += `<img  src="data:image/png;base64,${pointInfo.point.data.thumbnail}"></img>`;
    }
    text += "</div>";

    return { text: `${pointInfo.argumentText}<br/> ${time}`, html: text, visible: true };
  }

  function getValueTypeColor(valueTypeId) {
    switch (valueTypeId) {
      case valueTypeCodes.VA:
        return "#4caf50";
      case valueTypeCodes.NVA:
        return "#ff9800";
      case valueTypeCodes.W:
        return "#f44336";
      default:
        return null;
    }
  }

  function customizePoint(pointInfo) {
    if (pointInfo.data.typeId === controlTypes.CAMERA && pointInfo.data.thumbnail !== null) {
      return { image: { url: "data:image/png;base64," + pointInfo.data.thumbnail, width: 50, height: 50 }, visible: true };
    } else {
      const color = getValueTypeColor(pointInfo.data.valueTypeId) ?? pointInfo.data.color;
      return { color: color, border: { color: "grey", width: 1, visible: true, dashStyle: "dot", opacity: 1 } };
    }
  }

  function convertToTimeString(time) {
    let dateTime = new Date(time);
    let strTime = dateTime.toTimeString();
    strTime = strTime.split(" ")[0];
    return strTime;
  }

  function customizeText(e) {
    let tempLabelObj = labelData;
    let tempObj = {};
    let text = "";
    let color = "";
    if (e.point.data.color !== undefined) {
      color = getTextColor(e.point.data.color);
    } else {
      color = "black";
    }

    if (e.point.data.typeId === controlTypes.STOPWATCH || (e.point.data.typeId === controlTypes.EXTERNALAPI && e.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      //Stopwatch
      if (e.index === 0 && e.point.data.measurementValue !== "" && e.point.data.startDate !== null && e.point.data.endDate !== null) {
        let startDateTime = new Date(e.point.data.startDate);
        let endDateTime = new Date(e.point.data.endDate);
        let strStartTime = convertToTimeString(startDateTime);
        let strEndTime = convertToTimeString(endDateTime);

        tempObj = { id: e.point.data.id };
        tempLabelObj.push(tempObj);
        return `<div style="color: ${color}">${strStartTime} - ${strEndTime}</div>`;
      }
    } else {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    }
  }

  function measurementKeyLabel(e) {
    let tempLabelObj = labelData;
    let tempObj = {};
    let text = "";
    let color = "";
    if (e.point.data.color !== undefined) {
      color = getTextColor(e.point.data.color);
    } else {
      color = "black";
    }
    // Check if measurementPoint is a key value
    if (e.point.data.typeId === 3) {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    } else if (e.point.data.key !== null) {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.key + "</div>")}`;
    } else {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    }
  }

  useEffect(() => {
    if (props.from && props.to) {
      // Always honour the explicit range passed from the parent (zoom/scroll window)
      setVisualStartTime(new Date(props.from.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")));
      setVisualEndTime(new Date(props.to.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")));
    } else if (props.data && props.data.length > 0) {
      // Fallback: derive range from data
      const allStartDates = props.data.map((item) => new Date(item.startDate));
      const allEndDates = props.data.map((item) => new Date(item.endDate ?? item.startDate));
      const minDate = new Date(Math.min(...allStartDates));
      const maxDate = new Date(Math.max(...allEndDates));
      setVisualStartTime(new Date(minDate.getTime() - 60 * 1000));
      setVisualEndTime(new Date(maxDate.getTime() + 60 * 1000));
    }
  }, [props.data, props.from, props.to]);

  const chartRef = React.useRef(null);
  const zoomRangeRef = React.useRef(null);

  // Reset zoom when data or range changes
  useEffect(() => {
    zoomRangeRef.current = null;
  }, [props.data, props.from, props.to]);

  const handleOptionChanged = (e) => {
    if (e.fullName === "valueAxis[0].visualRange") {
      zoomRangeRef.current = { startValue: e.value.startValue, endValue: e.value.endValue };
    }
  };

  const isTimeBased = (item) =>
    item.typeId === controlTypes.STOPWATCH ||
    (item.typeId === controlTypes.EXTERNALAPI && item.externalApiTypeId === externalApiTypes.STOPWATCH);

  const filteredChartData = React.useMemo(() => {
    if (!props.data) return [];
    return chartView === "timeline"
      ? props.data.filter((x) => isTimeBased(x))
      : props.data.filter((x) => !isTimeBased(x));
  }, [props.data, chartView]); // eslint-disable-line react-hooks/exhaustive-deps

  const orderedResources = React.useMemo(() => {
    if (!props.registrationPoints || !props.registrationPoints.length) return [];
    return [...props.registrationPoints].reverse();
  }, [props.registrationPoints]);

  return (
    <>
      {loading && (
        <div className={classes.loadingOverlay}>
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner}></div>
            <p className={classes.loadingCard__title}>Loading chart...</p>
          </div>
        </div>
      )}
      <div className={classes.chartBox}>
        <div className={classes.viewToggle}>
          <button
            className={`${classes.viewToggleButton} ${chartView === "timeline" ? classes.viewToggleButton__active : ""}`}
            onClick={() => { setChartView("timeline"); props.onChartViewChange?.("timeline"); }}
          >
            Timeline
          </button>
          <button
            className={`${classes.viewToggleButton} ${chartView === "events" ? classes.viewToggleButton__active : ""}`}
            onClick={() => { setChartView("events"); props.onChartViewChange?.("events"); }}
          >
            Events
          </button>
        </div>
        {filteredChartData.length === 0 && props.data?.some(chartView === "timeline" ? (x) => !isTimeBased(x) : isTimeBased) && (
          <div className={classes.emptyStateContainer}>
            <div className={classes.emptyStateCard}>
              <p className={classes.emptyStateCard__title}>
                {chartView === "timeline"
                  ? "No timeline measurements in this range"
                  : "No events in this range"}
              </p>
              <p className={classes.emptyStateCard__subtitle}>
                {chartView === "timeline"
                  ? "There are events available for this period"
                  : "There are timeline measurements available for this period"}
              </p>
              <Button
                size="small"
                onClick={() => {
                  const next = chartView === "timeline" ? "events" : "timeline";
                  setChartView(next);
                  props.onChartViewChange?.(next);
                }}
              >
                {chartView === "timeline" ? "Switch to Events" : "Switch to Timeline"}
              </Button>
            </div>
          </div>
        )}
        <Chart
          key={chartView}
          id="chart"
          dataSource={filteredChartData}
          rotated={true}
          customizePoint={customizePoint}
          onOptionChanged={handleOptionChanged}
          ref={chartRef}
        >
          <CommonSeriesSettings
            type="rangeBar"
            value="measurementValue"
            argumentField="resource"
            rangeValue1Field="startDate"
            rangeValue2Field="endDate"
            barOverlapGroup="resource"
          ></CommonSeriesSettings>
          <Legend visible={false} />

          {/* SC: Timeline series - rangeBar for stopwatches */}
          <Series
            visible={chartView === "timeline"}
            type={visualisationType === timeLineVisualisationTypes.SPLIT ? "rangeBar" : "scatter"}
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "resource" : "linearTimeline"}
          />
          {/* SC: Events series - scatter for event-based data */}
          <Series
            visible={chartView === "events"}
            type="scatter"
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "resource" : "linearTimeline"}
          />
          <Tooltip enabled={true} customizeTooltip={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeTooltip : customizeLinearTooltip} />
          <ValueAxis
            visualRange={
              zoomRangeRef.current ?? {
                startValue: visualStartTime.toISOString(),
                endValue: visualEndTime.toISOString(),
              }
            }
          >
            <Grid visible={true} />
            <Label customizeText={customizeValueAxis} />
          </ValueAxis>
          <ArgumentAxis
            categories={orderedResources.length ? orderedResources : undefined}
            tickInterval={5}
          >
            <Grid visible={true} />
            <MinorGrid visible={true} />
          </ArgumentAxis>

          <Export enabled={true} />

          <CommonPaneSettings>
            <Border visible={true} />
          </CommonPaneSettings>
          <ZoomAndPan panKey="shift" argumentAxis="none" valueAxis="both" allowMouseWheel={true} dragToZoom="true" />
        </Chart>
      </div>
    </>
  );
}

const useStyles = makeStyles(() => ({
  chart: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  chartBox: {
    position: "relative",
    padding: "10px",
  },
  viewToggle: {
    display: "inline-flex",
    marginBottom: "8px",
    border: "1px solid var(--color-primary)",
    borderRadius: "4px",
    overflow: "hidden",
  },
  viewToggleButton: {
    padding: "6px 16px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    border: "none",
    backgroundColor: "transparent",
    color: "var(--color-primary)",
    cursor: "pointer",
    transition: "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#f5ede4",
    },
  },
  viewToggleButton__active: {
    backgroundColor: "var(--color-primary)",
    color: "white",
    "&:hover": {
      backgroundColor: "var(--color-primary)",
    },
  },
  switchButton: {
    marginTop: "12px",
    padding: "8px 20px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "var(--color-primary)",
    color: "white",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#b5885a",
    },
  },
  emptyStateContainer: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "998",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",

    textAlign: "center",

    margin: "0px",
    marginBottom: "12px",
  },

  loadingOverlay: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "999",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  loadingCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  loadingCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginTop: "16px",
  },

  loadingSpinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));
